import { useCallback, useMemo, useRef, useState } from 'react'
import type { ChartData, ChartOptions } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { IPlatformPeopleAccountsDashboard } from '@brushfire/types'
import '@/features/campaigns/charts/registerCharts'
import { PillSegmentToggle } from './PillSegmentToggle'
import { PLATFORM_CHART_CARD } from '../lib/platform-dashboard-layout'
import {
  computeNewSignupsYScale,
  formatNewSignupsDayLabel,
  formatNewSignupsTooltipDate,
  maxSignupCount,
  shouldShowNewSignupsXTick,
} from '../lib/new-signups-chart'

const { newSignupsByDay: chart } = PLATFORM_CHART_CARD

const axisFont = { family: 'Inter, sans-serif', size: chart.axisFontSize }

type SignupSeries = 'sensemakers' | 'signalProviders'

const SIGNUP_SERIES_OPTIONS = [
  { value: 'signalProviders' as const, label: 'Signal Providers' },
  { value: 'sensemakers' as const, label: 'Sensemakers' },
] as const

const CROSSFADE_MS = 125

interface Props {
  newSignupsByDay: IPlatformPeopleAccountsDashboard['newSignupsByDay']
}

export function NewSignupsByDayChart({ newSignupsByDay }: Props) {
  const [series, setSeries] = useState<SignupSeries>('sensemakers')
  const [chartVisible, setChartVisible] = useState(true)
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const buckets =
    series === 'sensemakers' ? newSignupsByDay.sensemakers : newSignupsByDay.signalProviders

  const barColor =
    series === 'sensemakers' ? chart.barSensemakers : chart.barSignalProviders

  const yScale = useMemo(
    () => computeNewSignupsYScale(maxSignupCount(buckets)),
    [buckets],
  )

  const handleSeriesChange = useCallback((next: SignupSeries) => {
    if (next === series) return
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
    setChartVisible(false)
    fadeTimerRef.current = setTimeout(() => {
      setSeries(next)
      setChartVisible(true)
      fadeTimerRef.current = null
    }, CROSSFADE_MS)
  }, [series])

  const data = useMemo<ChartData<'bar'>>(
    () => ({
      labels: buckets.map((b) => formatNewSignupsDayLabel(b.date)),
      datasets: [
        {
          label: series === 'sensemakers' ? 'Sensemakers' : 'Signal Providers',
          data: buckets.map((b) => b.count),
          backgroundColor: barColor,
          borderRadius: { topLeft: chart.barRadius, topRight: chart.barRadius },
          borderSkipped: 'bottom',
          maxBarThickness: 28,
        },
      ],
    }),
    [barColor, buckets, series],
  )

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 0, right: 4, top: 4, bottom: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: buckets.length > 0,
          backgroundColor: '#fff',
          borderWidth: 1.5,
          borderColor: barColor,
          titleColor: '#000',
          bodyColor: '#000',
          titleFont: {
            family: '"Plus Jakarta Sans", Inter, sans-serif',
            size: 12,
            weight: 'normal',
          },
          bodyFont: {
            family: '"Plus Jakarta Sans", Inter, sans-serif',
            size: 12,
            weight: 'bold',
          },
          padding: 10,
          cornerRadius: 10,
          displayColors: false,
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex ?? 0
              const date = buckets[idx]?.date
              return date ? formatNewSignupsTooltipDate(date) : items[0]?.label ?? ''
            },
            label: (ctx) => String(ctx.parsed.y ?? 0),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: chart.axisLabel,
            font: axisFont,
            maxRotation: 0,
            autoSkip: false,
            callback: (_value, index) => {
              if (!shouldShowNewSignupsXTick(index, buckets.length)) return ''
              return buckets[index] ? formatNewSignupsDayLabel(buckets[index].date) : ''
            },
          },
        },
        y: {
          min: 0,
          max: yScale.max,
          beginAtZero: true,
          grid: {
            color: chart.grid,
            borderDash: [3, 3],
            drawBorder: false,
            tickLength: 0,
          },
          border: { display: false },
          ticks: {
            color: chart.axisLabel,
            font: axisFont,
            stepSize: yScale.stepSize,
            precision: 0,
            padding: 6,
          },
        },
      },
    }),
    [barColor, buckets, yScale.max, yScale.stepSize],
  )

  return (
    <Box
      data-testid="platform-new-signups-chart"
      sx={{
        border: chart.border,
        borderRadius: chart.borderRadius,
        boxShadow: chart.boxShadow,
        pt: chart.paddingTop,
        pb: chart.paddingBottom,
        pl: chart.paddingLeft,
        pr: chart.paddingRight,
        bgcolor: chart.bgcolor,
        height: chart.height,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: chart.titleToBodyGap,
        }}
      >
        <Typography
          sx={{
            fontSize: chart.titleFontSize,
            fontWeight: chart.titleFontWeight,
            fontFamily: chart.titleFontFamily,
            color: chart.titleColor,
            letterSpacing: chart.titleLetterSpacing,
            lineHeight: chart.titleLineHeight,
            flex: 1,
            minWidth: 0,
          }}
        >
          New Sign-ups by Day
        </Typography>
        <PillSegmentToggle
          value={series}
          onChange={handleSeriesChange}
          options={SIGNUP_SERIES_OPTIONS}
          testId="platform-new-signups-series-toggle"
          variant="chart"
          width={chart.toggleWidth}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          opacity: chartVisible ? 1 : 0,
          transition: 'opacity 0.25s ease',
        }}
      >
        <Bar data={data} options={options} />
      </Box>
    </Box>
  )
}
